/**
 * INTERNAL live-object NodeViews (reminder chip · task/decision/truth cards). Makes the OQ-6
 * objects LIVE in the editing view: inline summary editing, a done-toggle on tasks, remove, and
 * the 'Run with AI' affordance (task cards only) that hands the host an {@link ObjectActionRequest}.
 *
 * STRICTLY INTERNAL — PM types stay in this file. The schema's `toDOM` is untouched, so
 * projections/copy/paste keep working; these views only affect the live editing surface. Every
 * mutation routes through the narrow {@link ObjectViewHost} slice of the engine, so each change
 * is exactly ONE attributed act-lane transaction (the same path as the public `updateObject`).
 */

import type { Node as PMNode } from 'prosemirror-model';
import type { EditorView, NodeView } from 'prosemirror-view';
import { getLiveObject } from '../nodes/registry';
import type { ObjectActionRequest } from '../types';

/** The narrow slice of the engine the views may touch (keeps NodeView↔engine coupling clean). */
export interface ObjectViewHost {
  /** Patch summary/data of the live-object node at `pos` as one attributed act transaction. */
  updateObjectAtPos(pos: number, patch: { summary?: string; data?: Record<string, unknown> }): void;
  /** Delete the live-object node at `pos` as one attributed act transaction. */
  removeObjectAtPos(pos: number): void;
  /** Host 'Run with AI' callback — fired ONLY by the task card's AI button. */
  onObjectAction?(req: ObjectActionRequest): void;
}

/** chip = inline reminder pill · task = card with done-checkbox + AI button · card = plain card. */
type Variant = 'chip' | 'task' | 'card';

type ViewFactory = (node: PMNode, view: EditorView, getPos: () => number | undefined) => NodeView;

/** Build the `nodeViews` dict handed to the EditorView config (registered by `EditorCore.mount`). */
export function buildObjectNodeViews(host: ObjectViewHost): Record<string, ViewFactory> {
  const make = (variant: Variant): ViewFactory => (node, _view, getPos) =>
    new LiveObjectView(node, getPos, host, variant);
  return {
    reminder_chip: make('chip'),
    task_card: make('task'),
    decision_card: make('card'),
    truth_card: make('card'),
  };
}

class LiveObjectView implements NodeView {
  readonly dom: HTMLElement;
  private node: PMNode;
  private readonly summaryEl: HTMLSpanElement;
  private checkbox: HTMLInputElement | null = null;
  private input: HTMLInputElement | null = null;

  constructor(
    node: PMNode,
    private readonly getPos: () => number | undefined,
    private readonly host: ObjectViewHost,
    private readonly variant: Variant,
  ) {
    this.node = node;
    this.dom = document.createElement(variant === 'chip' ? 'span' : 'div');
    this.dom.className = variant === 'chip' ? 'docit-object docit-object--chip' : 'docit-object docit-object--card';
    // The whole view is chrome, not editable text; only the summary <input> ever takes input.
    this.dom.contentEditable = 'false';
    this.summaryEl = document.createElement('span');
    this.summaryEl.className = 'docit-object__summary';
    if (variant === 'chip') this.buildChip();
    else this.buildCard();
    this.sync();
  }

  /** Handle attr changes (summary/data) from remote peers and our own transactions. */
  update(node: PMNode): boolean {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.sync();
    return true;
  }

  /** All inner DOM (header, controls, edit input) is ours — PM must never re-parse it. */
  ignoreMutation(): boolean {
    return true;
  }

  /** Events on the controls belong to the view, not the editor (no NodeSelection on click). */
  stopEvent(event: Event): boolean {
    const target = event.target as HTMLElement | null;
    return Boolean(target && typeof target.closest === 'function' && target.closest('button, input'));
  }

  // ── DOM construction ──

  /** Inline chip: [clock glyph · summary · edit · x], compact. */
  private buildChip(): void {
    const glyph = document.createElement('span');
    glyph.className = 'docit-object__glyph';
    glyph.textContent = '⏰';
    this.dom.append(
      glyph,
      this.summaryEl,
      this.makeButton('✎', 'Edit', 'docit-object__btn--edit', () => this.beginEdit()),
      this.makeButton('✕', 'Remove', 'docit-object__btn--remove', () => this.remove()),
    );
  }

  /** Card: header [kind label · spacer · (AI) · edit · remove], body = (checkbox +) summary. */
  private buildCard(): void {
    const header = document.createElement('div');
    header.className = 'docit-object__header';

    const label = document.createElement('span');
    label.className = 'docit-object__label';
    label.textContent = getLiveObject(String(this.node.attrs.kind ?? ''))?.label ?? String(this.node.attrs.kind ?? '');

    const spacer = document.createElement('span');
    spacer.className = 'docit-object__spacer';

    header.append(label, spacer);
    if (this.variant === 'task') {
      header.append(this.makeButton('AI', 'Run with AI', 'docit-object__btn--ai', () => this.fireObjectAction()));
    }
    header.append(
      this.makeButton('✎', 'Edit', 'docit-object__btn--edit', () => this.beginEdit()),
      this.makeButton('✕', 'Remove', 'docit-object__btn--remove', () => this.remove()),
    );

    const body = document.createElement('div');
    body.className = 'docit-object__body';
    if (this.variant === 'task') {
      this.checkbox = document.createElement('input');
      this.checkbox.type = 'checkbox';
      this.checkbox.className = 'docit-object__check';
      this.checkbox.title = 'Done';
      this.checkbox.setAttribute('aria-label', 'Done');
      // preventDefault keeps the editor selection intact; the click default still toggles.
      this.checkbox.addEventListener('mousedown', (e) => e.preventDefault());
      this.checkbox.addEventListener('change', () => this.toggleDone());
      body.append(this.checkbox);
    }
    body.append(this.summaryEl);

    this.dom.append(header, body);
  }

  private makeButton(text: string, title: string, cls: string, onClick: () => void): HTMLButtonElement {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = `docit-object__btn ${cls}`;
    btn.textContent = text;
    btn.title = title;
    // Glyph content ('✎'/'✕') would otherwise win the accessible-name computation.
    btn.setAttribute('aria-label', title);
    btn.contentEditable = 'false';
    // preventDefault on mousedown so clicking a control never destroys the editor selection.
    btn.addEventListener('mousedown', (e) => e.preventDefault());
    btn.addEventListener('click', onClick);
    return btn;
  }

  // ── State → DOM ──

  private sync(): void {
    const summary = String(this.node.attrs.summary ?? '');
    this.dom.setAttribute('data-object-kind', String(this.node.attrs.kind ?? ''));
    this.dom.setAttribute('data-object-id', String(this.node.attrs.objectId ?? ''));
    this.dom.setAttribute('data-object-summary', summary);
    this.summaryEl.textContent = summary;
    if (this.checkbox) {
      const done = Boolean((this.node.attrs.data as Record<string, unknown> | null)?.done);
      this.checkbox.checked = done;
      this.dom.setAttribute('data-object-done', String(done));
    }
  }

  // ── Affordances ──

  /** Swap the summary for an <input>; Enter/blur commits, Escape cancels. */
  private beginEdit(): void {
    if (this.input) {
      this.input.focus();
      return;
    }
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'docit-object__input';
    input.setAttribute('aria-label', 'Edit summary');
    input.value = String(this.node.attrs.summary ?? '');
    let cancelled = false;
    input.addEventListener('keydown', (e) => {
      // Keep editing keys (Enter/Escape/arrows/…) inside the input — never the editor keymaps.
      e.stopPropagation();
      if (e.key === 'Enter') {
        e.preventDefault();
        input.blur();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        cancelled = true;
        input.blur();
      }
    });
    input.addEventListener('blur', () => {
      const value = input.value;
      this.endEdit();
      if (!cancelled) this.commitSummary(value);
    });
    this.input = input;
    this.summaryEl.replaceWith(input);
    input.focus();
    input.select();
  }

  private endEdit(): void {
    if (!this.input) return;
    this.input.replaceWith(this.summaryEl);
    this.input = null;
  }

  /** Same path as the public `updateObject` — one attributed act transaction via the host. */
  private commitSummary(value: string): void {
    if (value === String(this.node.attrs.summary ?? '')) return;
    const pos = this.getPos();
    if (pos === undefined) return;
    this.host.updateObjectAtPos(pos, { summary: value });
  }

  private toggleDone(): void {
    const pos = this.getPos();
    if (pos === undefined || !this.checkbox) return;
    const data = { ...(this.node.attrs.data as Record<string, unknown>), done: this.checkbox.checked };
    this.host.updateObjectAtPos(pos, { data });
  }

  private remove(): void {
    const pos = this.getPos();
    if (pos === undefined) return;
    this.host.removeObjectAtPos(pos);
  }

  /** 'Run with AI' — hand the host a plain snapshot plus the insert-after position. */
  private fireObjectAction(): void {
    const pos = this.getPos();
    if (pos === undefined) return;
    this.host.onObjectAction?.({
      objectId: String(this.node.attrs.objectId ?? ''),
      kind: String(this.node.attrs.kind ?? ''),
      summary: String(this.node.attrs.summary ?? ''),
      data: { ...(this.node.attrs.data as Record<string, unknown>) },
      posAfter: pos + this.node.nodeSize,
    });
  }
}
